import { useCallback, useEffect, useState } from "react";

import { RequestResult } from "../create_action";
import { retryAfterSeconds } from "../request";

const TOO_MANY_REQUESTS_STATUS = 429;

// A 429 whose Retry-After the browser cannot read, such as one from a proxy in front of the API,
// still means wait. A longer wait than the ceiling is cut to it: the next try after it gets a fresh
// 429 carrying whatever is left, rather than a page disabled for a day.
const FALLBACK_WAIT_SECONDS = 60;
const MAX_WAIT_SECONDS = 3600;

/**
 * The wait a 429 asks for, in whole seconds, held until it runs out. `waitIfRateLimited` takes a
 * request's result and says whether it was a 429, so the page can skip reading it as a wrong input.
 */
export const useRateLimitWait = () => {
  const [wait, setWait] = useState<{ seconds: number } | null>(null);

  // a new object per 429, so a second one restarts the timer even when it asks for the same wait
  useEffect(() => {
    if (!wait) return;
    const timeout = setTimeout(() => setWait(null), wait.seconds * 1000);
    return () => clearTimeout(timeout);
  }, [wait]);

  const waitIfRateLimited = useCallback((requestResult: RequestResult): boolean => {
    const result = requestResult.result;
    if (result?.statusCode !== TOO_MANY_REQUESTS_STATUS) return false;

    const seconds = retryAfterSeconds(result.headers) ?? FALLBACK_WAIT_SECONDS;
    setWait({ seconds: Math.min(Math.max(seconds, 1), MAX_WAIT_SECONDS) });
    return true;
  }, []);

  return { waitSeconds: wait?.seconds ?? null, waitIfRateLimited };
};
